/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { inject, Injectable } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { Functions, httpsCallable, HttpsCallableOptions } from '@angular/fire/functions';
import { FirebaseError } from 'firebase/app';
import { AngularFireContextService } from './angular-fire-context.service';

enum ErrorCode {
	OK = 'ok',
	CANCELLED = 'cancelled',
	UNKNOWN = 'unknown',
	INVALID_ARGUMENT = 'invalid-argument',
	NOT_FOUND = 'not-found',
	ALREADY_EXISTS = 'already-exists',
	PERMISSION_DENIED = 'permission-denied',
	FAILED_PRECONDITION = 'failed-precondition',
	ABORTED = 'aborted',
	OUT_OF_RANGE = 'out-of-range',
	INTERNAL = 'internal',
	UNAVAILABLE = 'unavailable',
	UNAUTHENTICATED = 'unauthenticated'
}

@Injectable()
export class ApiFirebaseClient {
	public static readonly ErrorCode = ErrorCode;

	private readonly functions = inject(Functions);
	private readonly context = inject(AngularFireContextService);

	public call<D, R>(functionName: string, data?: D, options?: HttpsCallableOptions): Promise<R> {
		return this.context
			.run(() => httpsCallable<D, R>(this.functions, functionName, options)(data))
			.then(d => (d?.data ? this.convertTimestamps<R>(d.data) : d?.data))
			.catch((error: FirebaseError) => {
				// remove slash because firebase-admin add a prefix to errors
				const code = { code: error?.code?.substring(error?.code?.indexOf('/') + 1) };
				throw { ...error, ...code } as FirebaseError;
			});
	}

	private convertTimestamps<T>(data: T): T {
		if (!data) {
			return data;
		}

		if (Array.isArray(data)) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return data.map(i => this.convertTimestamps(i)) as T;
		}
		if (!(data instanceof Object)) {
			return data;
		}

		return Object.entries(data).reduce(
			(a: T, [k, v]) => (
				v instanceof Object && '_seconds' in v && '_nanoseconds' in v
					? (a[k as keyof typeof a] = this.toTimestamp(
							v as {
								_seconds: number;
								_nanoseconds: number;
							}
						) as any)
					: (a[k as keyof typeof a] = this.convertTimestamps<typeof v>(v)),
				a
			),
			data
		);
	}

	private toTimestamp(value: { _seconds: number; _nanoseconds: number }): Timestamp {
		return new Timestamp(value._seconds, value._nanoseconds);
	}
}
